import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SettingsIcon } from 'assets/header/SettingsIcon';
import Button from 'common-ui/button';
import Loading from 'common-ui/loading';
import { getLeadsKanban } from 'features/newsales/newsalesActions';

import AssignLeadModal from '../components/assignLeadModal';
import TakeLeadModal from '../components/takeLeadModal/TakeLeadModal';
import { Container, ContentWrapper, ErrorContainer, Header, Title } from './MyLeads.styles';
import KanbanBoard from './components/KanbanBoard';
import StatsCards from './components/StatsCards';
import SearchAndFilter from './components/searchAndFilter';
import useSearchData from './useSearchData';

const MyLeads = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { searchData, setSearchData, resetSearchData } = useSearchData();
  const [columns, setColumns] = useState({});
  const [columnDefinitions, setColumnDefinitions] = useState([]);
  const [columnPagination, setColumnPagination] = useState({});
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalConverted: 0,
    totalValue: '0 AMD',
    conversionRate: '0%',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openTakeModal, setOpenTakeModal] = useState(false);
  const [mode, setMode] = useState('assign');

  const formatFullAddress = (lead) => {
    const addressParts = [];

    if (lead.country?.name) addressParts.push(lead.country.name);
    if (lead.district?.name) addressParts.push(lead.district.name);
    if (lead.city?.name) addressParts.push(lead.city.name);
    if (lead.street?.name) addressParts.push(lead.street.name);
    if (lead.buildingHouse?.name) addressParts.push(lead.buildingHouse.name);

    return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
  };

  const fetchKanbanData = useCallback(async () => {
    setError(null);

    setIsLoading(false);

    try {
      const response = await dispatch(getLeadsKanban(searchData)).unwrap();

      const dynamicColumns = {};
      const dynamicColumnDefs = [];
      const dynamicPagination = {};

      if (response.columns && Array.isArray(response.columns)) {
        response.columns.forEach((columnData) => {
          const workflowStatus = columnData.workflowStatus;
          const columnId = workflowStatus.id.toString();

          dynamicColumnDefs.push({
            id: columnId,
            title: workflowStatus.name,
            color: workflowStatus.color || '#3b82f6',
            outcomeType: workflowStatus.outcomeType,
          });

          const transformedLeads = columnData.leads.map((lead) => ({
            ...lead,
            id: lead.id.toString(),
            companyName:
              lead.businessType === 'B2B'
                ? lead.companyName
                : `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'N/A',
            address: formatFullAddress(lead),
            assigneeUuid: lead.assigneeUuid,
            priority: lead.priority || 'none',
            status: columnId,
            workflowStatusId: workflowStatus.id,
          }));

          dynamicColumns[columnId] = transformedLeads;

          // Store pagination info for each column
          if (columnData.pagination) {
            dynamicPagination[columnId] = columnData.pagination;
          }
        });
      }

      setColumnDefinitions(dynamicColumnDefs);
      setColumns(dynamicColumns);
      setColumnPagination(dynamicPagination);

      if (response.stats) {
        setStats({
          totalLeads: response.stats.totalLeads || 0,
          totalConverted: response.stats.totalConverted || 0,
          totalValue: `${response.stats.totalConvertedValue || 0} AMD`,
          conversionRate: `${response.stats.conversionRate || 0}%`,
        });
      }
    } catch (error) {
      setError(error);
      console.error('Failed to fetch kanban data:', error);
    }
  }, [dispatch, searchData]);

  const handleBulkAssign = () => {
    setMode('assign');
    setOpenAssignModal(true);
  };

  const handleBulkReassign = () => {
    setMode('reassign');
    setOpenAssignModal(true);
  };

  const handleTakeLead = () => {
    setOpenTakeModal(true);
  };

  const handleNavigateSettings = () => {
    navigate('/settings/sales-new-settings');
  };

  const userType = localStorage.getItem('userType');
  const canAccessSettings = userType === 'Super Admin' || userType === 'General Manager';

  const handleSuccess = async () => {
    await fetchKanbanData();
    setSelectedLeadIds([]);
  };

  useEffect(() => {
    fetchKanbanData();
  }, [fetchKanbanData]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>My Leads</Title>
        </Header>
        <ErrorContainer>Error loading kanban data: {error}</ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>My Leads</Title>
        {canAccessSettings && (
          <Button width={50} outlined onClick={handleNavigateSettings}>
            <SettingsIcon />
          </Button>
        )}
      </Header>

      <StatsCards stats={stats} />

      <SearchAndFilter
        columnDefinitions={columnDefinitions}
        selectedLeadIds={selectedLeadIds}
        columns={columns}
        setColumns={setColumns}
        setSearchData={setSearchData}
        resetSearchData={resetSearchData}
        onSelectionChange={setSelectedLeadIds}
        onAssign={handleBulkAssign}
        onReassign={handleBulkReassign}
        onTakeLead={handleTakeLead}
      />

      <ContentWrapper>
        <KanbanBoard
          columns={columns}
          setColumns={setColumns}
          columnDefinitions={columnDefinitions}
          columnPagination={columnPagination}
          selectedLeadIds={selectedLeadIds}
          onSelectionChange={setSelectedLeadIds}
          searchData={searchData}
        />
      </ContentWrapper>

      <AssignLeadModal
        leadData={selectedLeadIds}
        isOpen={openAssignModal}
        mode={mode}
        onSuccess={handleSuccess}
        onClose={() => setOpenAssignModal(false)}
      />
      <TakeLeadModal
        leadIds={selectedLeadIds}
        isOpen={openTakeModal}
        onSuccess={handleSuccess}
        onClose={() => setOpenTakeModal(false)}
      />
    </Container>
  );
};

export default MyLeads;
